<!-- @format -->
<template>
    <div class="header-widget">
        <div class="widget-container">
            <div class="widget-header">
                <h3>{{ widgetTitle }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <header-starting-step
                        v-if="currentStep === 'startingPoint'"
                        :template-options="templateOptions"
                        :selected-template-option="selectedCreateOption"
                        @input="handleInputCreateOption"
                    />
                    <header-select-template-step
                        v-if="currentStep === 'existingTemplate'"
                        :header="headerForm"
                        :client-id="clientId"
                    />
                    <header-settings-step
                        v-if="currentStep === 'settings'"
                        :account-id="accountId"
                        :is-loading="isLoading"
                        :header="headerForm"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <app-button class="cancel-btn transparent-btn" size="normal" @click="closeWidget">
                    Cancel
                </app-button>
                <div class="actions-container">
                    <app-button
                        v-if="hasBackAction && !isEditMode && !isReturnDisabled"
                        class="secondary-btn"
                        @click="goBack"
                    >
                        <span class="fa fa-arrow-left"></span>
                        Go Back
                    </app-button>
                    <app-button :disabled="isNextDisabled || isLoading" @click="goNext">
                        Next Step
                        <span class="fa fa-arrow-right"></span>
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import PopUpMixin from '@/mixins/pop-up-mixin';
import { LandingPageSectionService } from '@/scripts/services/landing-page/landing-page-section-service';
import { BrandingService } from '@/scripts/services/magic-branding/branding-service';

const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const HeaderSettingsStep = () =>
    import('@/components/sliding-widgets/headers/header-settings-step');
const HeaderStartingStep = () =>
    import('@/components/sliding-widgets/headers/header-starting-step');
const HeaderSelectTemplateStep = () =>
    import('@/components/sliding-widgets/headers/header-select-template-step');

export default {
    name: 'header-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'header-settings-step': HeaderSettingsStep,
        'header-starting-step': HeaderStartingStep,
        'header-select-template-step': HeaderSelectTemplateStep,
    },

    mixins: [PopUpMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            headerForm: {
                name: '',
                categories: [],
                configuration: {
                    structure: {
                        name: '',
                        logoAndMenuPosition: 'logo-left-menu-right',
                        isTopbarEnabled: true,
                        isLogoLeftMenuRight: true,
                        isLogoRightMenuLeft: false,
                        isLogoLeftMenuBelow: false,
                        isLogoRightMenuBelow: false,
                        isLogoCenterMenuBelow: false,
                        isLogoCenterMenuAbove: false,
                    },

                    topbar: {
                        style: {
                            fontColor: { value: '#ffffff', branding: null, name: null },
                            linkColor: { value: '#ffffff', branding: null, name: null },
                            backgroundColor: { branding: null, name: null, value: '#382153' },
                            fontSize: '17px',
                            fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
                        },

                        leftColumn: {
                            type: 'text',
                            text: 'This is a cool text',
                            menu: [],
                            isText: true,
                            isMenu: false,
                        },

                        rightColumn: {
                            type: 'menu',
                            text: null,
                            menu: [
                                { name: 'Support', url: '' },
                                { name: 'Call Us', url: '' },
                            ],

                            isText: false,
                            isMenu: true,
                        },
                    },

                    header: {
                        style: {
                            fontSize: '20px',
                            fontFamily: 'Nunito, sans-serif',
                            fontColor: { value: '#382153', branding: null, name: null },
                            linkColor: { value: '#382153', branding: null, name: null },
                            backgroundColor: { value: '#ffffff', branding: null, name: null },
                        },

                        logo: { url: null, name: null, type: null },
                        menu: [
                            { name: 'Home', url: '' },
                            { name: 'About Us', url: '' },
                            { name: 'Services', url: '' },
                            { name: 'Contact', url: '' },
                        ],
                    },
                },
            },

            isLoading: false,
            selectedCreateOption: null,
            currentStep: 'startingPoint',
            lastActiveStep: null,
            promoId: null,
            isReturnDisabled: false,

            templateOptions: [
                {
                    imageUrl: require('@/assets/images/headers-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
                {
                    imageUrl: require('@/assets/images/headers-templates.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
            ],

            steps: {
                startingPoint: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedCreateOption.value);
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Select a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.formConfig = {
                                    ...this.defaultFormConfig,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create Header Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.saveHeader();
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.widget.options.accountId;
        },

        clientId() {
            return this.widget.options.clientId;
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        headerId() {
            return this.widget.options.headerId;
        },

        isFromTemplate() {
            return this.widget.options.isFromTemplate;
        },

        isLandingPage() {
            return this.widget.options.isLandingPage;
        },

        widgetTitle() {
            if (this.isEditMode) {
                return 'Edit Header';
            }

            return this.steps[this.currentStep].title;
        },

        isEditMode() {
            return (
                this.widget.options.headerId !== null && this.widget.options.headerId !== undefined
            );
        },

        isNextDisabled() {
            if (this.currentStep === 'settings' || this.currentStep === 'existingTemplate') {
                return this.headerForm.name.length <= 0;
            }
            return false;
        },

        hasBackAction() {
            return (
                this.steps[this.currentStep].actions.backAction !== undefined &&
                !this.isFromTemplate
            );
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        headerConfig() {
            return this.headerForm.configuration;
        },
    },

    created() {
        if (this.isEditMode || this.isFromTemplate) {
            this.getHeaderData();
            this.goToStep('settings');
        } else if (this.clientId) {
            this.isLoading = true;

            this.getBranding()
                .then(this.getPromoId)
                .then(() => {
                    Promise.all(this.getTotalHeadersCount())
                        .then((counts) => {
                            const total = counts.reduce((acc, count) => acc + count, 0);

                            if (total === 0) {
                                this.isReturnDisabled = true;
                                return this.goToStep('settings');
                            }
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification(
                                'Failed to get total header templates.',
                                { error },
                            );
                        })
                        .finally(() => {
                            this.isLoading = false;
                        });
                });
        } else {
            this.isLoading = true;

            const filter = `Type eq TriDigital.EntityDomain.Entities.LandingPageSectionType'Header' and AccountId eq ${this.accountId} and (SharedAssets/any() or not SharedAssets/any())&$top=0`;

            this.getTemplatesCount(filter)
                .then((count) => {
                    if (count === 0) {
                        this.isReturnDisabled = true;
                        return this.goToStep('settings');
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get total header templates.', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }

        this.selectedCreateOption = this.templateOptions[0];
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleInputCreateOption(createOptionSelected) {
            this.selectedCreateOption = createOptionSelected;
        },

        getHeaderData() {
            this.isLoading = true;

            LandingPageSectionService.get(this.headerId)
                .then(({ data }) => {
                    this.headerForm = {
                        name: data.value[0].Name,
                        categories: this.isFromTemplate
                            ? []
                            : data.value[0].Categories.map(({ Name }) => Name),
                        configuration: JSON.parse(data.value[0].Configuration),
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while getting your header data.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        createHeader() {
            const isUsingTemplate =
                this.isFromTemplate || this.selectedCreateOption.value === 'existingTemplate';
            const body = {
                name: this.headerForm.name,
                categories: this.headerForm.categories,
                configuration: JSON.stringify(this.headerForm.configuration),
                accountId: this.accountId,
                type: 'header',
            };

            LandingPageSectionService.post(body)
                .then(({ data }) => {
                    this.$helpers.showSuccessNotification(
                        isUsingTemplate
                            ? 'Your header has been created'
                            : 'Your header has been created with default settings.',
                    );

                    this.$router.push(`${this.baseUrl}/LandingPages/Headers/${data.Id}`);
                    EventBus.$emit('close-widgets');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while creating your header.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        patchHeader() {
            this.headerConfig.structure.name = this.headerForm.name;

            const body = [
                {
                    name: 'Name',
                    value: this.headerForm.name,
                },
                {
                    name: 'Categories',
                    value: this.headerForm.categories,
                },
                {
                    name: 'Configuration',
                    value: JSON.stringify(this.headerForm.configuration),
                },
            ];

            LandingPageSectionService.patch(this.headerId, body)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your header has been updated with default settings.',
                    );

                    this.$router.push(`${this.baseUrl}/LandingPages/Headers/${this.headerId}`);
                    EventBus.$emit('close-widgets');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while updating your header.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        saveHeader() {
            if (this.isLandingPage) {
                return this.showCreateConfirmPopup();
            }

            this.isLoading = true;

            if (this.isEditMode && !this.isFromTemplate) {
                this.patchHeader();
            } else {
                this.createHeader();
            }
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        getTemplatesCount(filter) {
            return new Promise((resolve, reject) => {
                LandingPageSectionService.getAll({
                    filter: filter,
                    count: true,
                })
                    .then(({ data }) => {
                        const totalTemplates = data['@odata.count'];

                        resolve(totalTemplates);
                    })
                    .catch(reject);
            });
        },

        getTotalHeadersCount() {
            const myTemplatesCountfilter = `Type eq TriDigital.EntityDomain.Entities.LandingPageSectionType'Header' and  (AccountId eq ${this.accountId})&$top=0`;
            const sharedTemplatesCountfilter = `Type eq TriDigital.EntityDomain.Entities.LandingPageSectionType'Header' and SharedAssets/any(a: a/AgencyClient/ClientId eq  ${this.clientId} or a/PromoId eq ${this.promoId})&$top=0`;

            return [
                this.getTemplatesCount(myTemplatesCountfilter),
                this.getTemplatesCount(sharedTemplatesCountfilter),
            ];
        },

        getPromoId() {
            return new Promise((resolve, reject) => {
                LandingPageSectionService.getClient(this.clientId, {
                    select: 'PromoId',
                })
                    .then((response) => {
                        this.promoId = response.data.value[0].PromoId;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Failed to get total header templates.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getBranding() {
            const filter = `clientId=${this.clientId}`;

            return new Promise((resolve, reject) => {
                BrandingService.get(filter)
                    .then(({ data }) => {
                        const branding = JSON.parse(data.Configuration);

                        this.headerConfig.topbar.style.fontColor.value =
                            branding.colors.fontColors.bodyText.value;
                        this.headerConfig.topbar.style.fontSize = `${branding.fonts.landingPage.bodyText.fontSize}px`;
                        this.headerConfig.topbar.style.linkColor.value =
                            branding.colors.fontColors.linkColor.value;
                        this.headerConfig.topbar.style.backgroundColor.value =
                            branding.colors.landingPageColors.backgroundColor.value;
                        this.headerConfig.topbar.style.fontFamily =
                            branding.fonts.landingPage.bodyText.fontFamily.value;

                        this.headerConfig.header.style.fontColor.value =
                            branding.colors.fontColors.bodyText.value;
                        this.headerConfig.header.style.fontSize = `${branding.fonts.landingPage.bodyText.fontSize}px`;
                        this.headerConfig.header.style.linkColor.value =
                            branding.colors.fontColors.linkColor.value;
                        this.headerConfig.header.style.backgroundColor.value =
                            branding.colors.landingPageColors.backgroundColor.value;
                        this.headerConfig.header.style.fontFamily =
                            branding.fonts.landingPage.bodyText.fontFamily.value;

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'There was an error retrieving your branding',
                            { error },
                        );
                        reject();
                    });
            });
        },

        showCreateConfirmPopup() {
            const actions = {
                confirm: {
                    label: 'Ok',
                    action: () => {
                        this.createHeader();
                    },
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            const modalProps = {
                title: 'Are you sure?',
                content: '<p>By doing this you will lose your unsaved progress.</p>',
                actions,
            };

            this.showPopup(modalProps, 'alert-popup');
        },
    },
};
</script>

<style>
.header-widget .widget-container {
    width: 100%;
    height: 100%;
}

.header-widget .widget-container .content-wrapper {
    padding: 23px 29px 23px 33px;
}

.header-widget .content-wrapper .loading-screen {
    text-align: center;
}

.header-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.header-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.header-widget .save-btn {
    background: #18aeff;
}

.header-widget .save-btn:hover {
    background: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.header-widget .widget-footer .actions-container {
    display: flex;
}

.header-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid #18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.header-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.header-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
